import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setMainHeading } from '../../../../redux/modules/mainHeading/slice';
import { getAllLabReagentRequest, resetGetAllLabReagent, createLabReagentRequest, resetCreateLabReagent, updateLabReagentRequest, resetUpdateLabReagent, adjustReagentQuantityRequest, resetAdjustReagentQuantity, getReagentAlertsRequest, resetGetReagentAlerts, getUsageHistoryRequest, resetGetUsageHistory, getDepletionSummaryRequest } from '../../../../redux/modules/laboratory/labReagent/slice';
import { getLabDepartmentRequest } from '../../../../redux/modules/laboratory/labDepartment/slice';
import { getLabTestRequest } from '../../../../redux/modules/laboratory/labTest/slice';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Button, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, IconButton, Tooltip, Alert, Badge, Autocomplete, TablePagination } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HistoryIcon from '@mui/icons-material/History';
import { toast } from 'react-toastify';

export default function LabInventory() {
    const dispatch = useDispatch();
    const { getAllRes, createRes, updateRes, adjustQuantityRes, getAlertsRes, usageHistoryRes, depletionSummaryRes, loading } = useSelector(state => state.labReagent);
    const { getRes: deptRes } = useSelector(state => state.labDepartment);
    const { getRes: testRes } = useSelector(state => state.labTest);

    const [deptFilter, setDeptFilter] = useState('');
    const [createDialog, setCreateDialog] = useState(false);
    const [editDialog, setEditDialog] = useState(false);
    const [adjustDialog, setAdjustDialog] = useState(false);
    const [alertsDialog, setAlertsDialog] = useState(false);
    const [historyDialog, setHistoryDialog] = useState(false);
    const [selectedReagent, setSelectedReagent] = useState(null);
    const [historyPage, setHistoryPage] = useState(0);
    const [historyRowsPerPage, setHistoryRowsPerPage] = useState(10);

    const [form, setForm] = useState({
        name: '', catalogNumber: '', manufacturer: '', supplier: '', lotNumber: '',
        expiryDate: '', receivedDate: '', quantityReceived: '', unit: 'tests',
        reorderPoint: '', reorderQuantity: '', costPerUnit: '', currency: 'GYD',
        storageTemp: '', storageLocation: '', departmentId: '',
        associatedTests: [], expiryAlertDays: 30,
    });
    const [adjustForm, setAdjustForm] = useState({ adjustment: '', reason: '' });

    useEffect(() => {
        dispatch(setMainHeading("Reagent Inventory"));
        dispatch(getAllLabReagentRequest({}));
        dispatch(getLabDepartmentRequest());
        dispatch(getReagentAlertsRequest());
        dispatch(getDepletionSummaryRequest());
        dispatch(getLabTestRequest());
    }, [dispatch]);

    useEffect(() => {
        if (createRes?.success) {
            toast.success("Reagent created");
            setCreateDialog(false);
            setForm({ name: '', catalogNumber: '', manufacturer: '', supplier: '', lotNumber: '', expiryDate: '', receivedDate: '', quantityReceived: '', unit: 'tests', reorderPoint: '', reorderQuantity: '', costPerUnit: '', currency: 'GYD', storageTemp: '', storageLocation: '', departmentId: '', associatedTests: [], expiryAlertDays: 30 });
            dispatch(resetCreateLabReagent());
            dispatch(getAllLabReagentRequest({}));
            dispatch(getReagentAlertsRequest());
            dispatch(getDepletionSummaryRequest());
        } else if (createRes && !createRes.success) {
            toast.error(createRes.message || "Failed to create reagent");
            dispatch(resetCreateLabReagent());
        }
    }, [createRes, dispatch]);

    useEffect(() => {
        if (adjustQuantityRes?.success) {
            toast.success(adjustQuantityRes.message || "Quantity adjusted");
            setAdjustDialog(false);
            setAdjustForm({ adjustment: '', reason: '' });
            dispatch(resetAdjustReagentQuantity());
            dispatch(getAllLabReagentRequest({}));
            dispatch(getReagentAlertsRequest());
            dispatch(getDepletionSummaryRequest());
        } else if (adjustQuantityRes && !adjustQuantityRes.success) {
            toast.error(adjustQuantityRes.message || "Adjustment failed");
            dispatch(resetAdjustReagentQuantity());
        }
    }, [adjustQuantityRes, dispatch]);

    useEffect(() => {
        if (updateRes?.success) {
            toast.success("Reagent updated");
            setEditDialog(false);
            dispatch(resetUpdateLabReagent());
            dispatch(getAllLabReagentRequest({}));
            dispatch(getReagentAlertsRequest());
            dispatch(getDepletionSummaryRequest());
        } else if (updateRes && !updateRes.success) {
            toast.error(updateRes.message || "Failed to update reagent");
            dispatch(resetUpdateLabReagent());
        }
    }, [updateRes, dispatch]);

    const reagents = getAllRes?.data || [];
    const departments = deptRes?.data || [];
    const tests = testRes?.data || [];
    const alerts = getAlertsRes?.data || {};
    const depletionData = depletionSummaryRes?.data || {};
    const totalAlerts = (alerts.lowStock?.length || 0) + (alerts.expiringSoon?.length || 0) + (alerts.expired?.length || 0);
    const filtered = deptFilter ? reagents.filter(r => r.departmentId?._id === deptFilter) : reagents;

    const handleCreate = () => {
        const payload = { ...form };
        if (payload.quantityReceived) payload.quantityReceived = Number(payload.quantityReceived);
        if (payload.reorderPoint) payload.reorderPoint = Number(payload.reorderPoint);
        if (payload.reorderQuantity) payload.reorderQuantity = Number(payload.reorderQuantity);
        if (payload.costPerUnit) payload.costPerUnit = Number(payload.costPerUnit);
        if (payload.expiryAlertDays) payload.expiryAlertDays = Number(payload.expiryAlertDays);
        dispatch(createLabReagentRequest(payload));
    };

    const handleOpenEdit = (reagent) => {
        setSelectedReagent(reagent);
        setForm({
            name: reagent.name || '',
            catalogNumber: reagent.catalogNumber || '',
            manufacturer: reagent.manufacturer || '',
            supplier: reagent.supplier || '',
            lotNumber: reagent.lotNumber || '',
            expiryDate: reagent.expiryDate ? reagent.expiryDate.substring(0, 10) : '',
            receivedDate: reagent.receivedDate ? reagent.receivedDate.substring(0, 10) : '',
            quantityReceived: reagent.quantityReceived ?? '',
            unit: reagent.unit || 'tests',
            reorderPoint: reagent.reorderPoint ?? '',
            reorderQuantity: reagent.reorderQuantity ?? '',
            costPerUnit: reagent.costPerUnit ?? '',
            currency: reagent.currency || 'GYD',
            storageTemp: reagent.storageTemp || '',
            storageLocation: reagent.storageLocation || '',
            departmentId: reagent.departmentId?._id || reagent.departmentId || '',
            associatedTests: (reagent.associatedTests || []).map(a => ({
                testId: a.testId?._id || a.testId,
                testLabel: a.testId?.name ? `${a.testId.testCode || ''} - ${a.testId.name}` : (a.testLabel || a.testId),
                consumptionPerTest: a.consumptionPerTest ?? 1,
            })),
            expiryAlertDays: reagent.expiryAlertDays ?? 30,
        });
        setEditDialog(true);
    };

    const handleUpdate = () => {
        const payload = { ...form };
        if (payload.quantityReceived) payload.quantityReceived = Number(payload.quantityReceived);
        if (payload.reorderPoint) payload.reorderPoint = Number(payload.reorderPoint);
        if (payload.reorderQuantity) payload.reorderQuantity = Number(payload.reorderQuantity);
        if (payload.costPerUnit) payload.costPerUnit = Number(payload.costPerUnit);
        if (payload.expiryAlertDays) payload.expiryAlertDays = Number(payload.expiryAlertDays);
        dispatch(updateLabReagentRequest({ id: selectedReagent._id, ...payload }));
    };

    const handleAdjust = () => {
        dispatch(adjustReagentQuantityRequest({
            id: selectedReagent._id,
            adjustment: Number(adjustForm.adjustment),
            reason: adjustForm.reason,
        }));
    };

    const handleOpenHistory = (reagent) => {
        setSelectedReagent(reagent);
        setHistoryPage(0);
        setHistoryDialog(true);
        dispatch(getUsageHistoryRequest({ id: reagent._id, page: 1, limit: historyRowsPerPage }));
    };

    const handleAddAssociatedTest = (event, newValue) => {
        if (!newValue) return;
        if (form.associatedTests.find(a => a.testId === newValue._id)) return;
        setForm({
            ...form,
            associatedTests: [...form.associatedTests, { testId: newValue._id, testLabel: `${newValue.testCode} - ${newValue.name}`, consumptionPerTest: 1 }]
        });
    };

    const handleRemoveAssociatedTest = (testId) => {
        setForm({ ...form, associatedTests: form.associatedTests.filter(a => a.testId !== testId) });
    };

    const handleConsumptionChange = (testId, value) => {
        setForm({
            ...form,
            associatedTests: form.associatedTests.map(a =>
                a.testId === testId ? { ...a, consumptionPerTest: Number(value) || 1 } : a
            )
        });
    };

    const getStockChip = (reagent) => {
        if (!reagent.reorderPoint) return null;
        if (reagent.quantityRemaining <= 0) return <Chip label="Out of Stock" color="error" size="small" />;
        if (reagent.quantityRemaining <= reagent.reorderPoint) return <Chip label="Low Stock" color="warning" size="small" />;
        return <Chip label="In Stock" color="success" size="small" />;
    };

    const getExpiryChip = (reagent) => {
        if (!reagent.expiryDate) return null;
        const expiry = new Date(reagent.expiryDate);
        const now = new Date();
        const alertDays = reagent.expiryAlertDays || 30;
        const threshold = new Date();
        threshold.setDate(now.getDate() + alertDays);
        if (expiry <= now) return <Chip label="Expired" color="error" size="small" />;
        if (expiry <= threshold) return <Chip label="Expiring Soon" color="warning" size="small" />;
        return null;
    };

    const getDepletionChip = (reagentId) => {
        const info = depletionData[reagentId];
        if (!info || info.estimatedDaysRemaining === null) return <Chip label="N/A" size="small" variant="outlined" />;
        const days = info.estimatedDaysRemaining;
        if (days < 7) return <Chip label={`${days}d`} color="error" size="small" />;
        if (days < 14) return <Chip label={`${days}d`} color="warning" size="small" />;
        return <Chip label={`${days}d`} color="success" size="small" />;
    };

    const changeTypeLabels = {
        test_consumption: 'Test Consumption',
        manual_adjustment: 'Manual Adjustment',
        restock: 'Restock',
        correction: 'Correction',
        expired_disposal: 'Expired Disposal',
    };

    const usageHistory = usageHistoryRes?.data || [];
    const historyPagination = usageHistoryRes?.pagination || { total: 0 };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Department</InputLabel>
                        <Select value={deptFilter} label="Department" onChange={e => setDeptFilter(e.target.value)}>
                            <MenuItem value="">All Departments</MenuItem>
                            {departments.map(d => <MenuItem key={d._id} value={d._id}>{d.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Badge badgeContent={totalAlerts} color="error">
                        <Button variant="outlined" color="warning" startIcon={<WarningAmberIcon />} onClick={() => setAlertsDialog(true)}>Alerts</Button>
                    </Badge>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialog(true)}>Add Reagent</Button>
            </Box>

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Lot #</TableCell>
                            <TableCell>Department</TableCell>
                            <TableCell>Manufacturer</TableCell>
                            <TableCell align="right">Qty Remaining</TableCell>
                            <TableCell>Unit</TableCell>
                            <TableCell>Stock Status</TableCell>
                            <TableCell>Expiry</TableCell>
                            <TableCell align="center">Est. Days</TableCell>
                            <TableCell>Storage</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filtered.map(r => (
                            <TableRow key={r._id} hover>
                                <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                                <TableCell>{r.lotNumber || '-'}</TableCell>
                                <TableCell>{r.departmentId?.name || '-'}</TableCell>
                                <TableCell>{r.manufacturer || '-'}</TableCell>
                                <TableCell align="right">{r.quantityRemaining ?? '-'}</TableCell>
                                <TableCell>{r.unit || '-'}</TableCell>
                                <TableCell>{getStockChip(r)}</TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {r.expiryDate ? new Date(r.expiryDate).toLocaleDateString() : '-'}
                                        {getExpiryChip(r)}
                                    </Box>
                                </TableCell>
                                <TableCell align="center">{getDepletionChip(r._id)}</TableCell>
                                <TableCell>{r.storageTemp || '-'}</TableCell>
                                <TableCell align="center">
                                    <Tooltip title="Edit Reagent">
                                        <IconButton size="small" color="info" onClick={() => handleOpenEdit(r)}><EditIcon fontSize="small" /></IconButton>
                                    </Tooltip>
                                    <Tooltip title="Usage History">
                                        <IconButton size="small" onClick={() => handleOpenHistory(r)}><HistoryIcon fontSize="small" /></IconButton>
                                    </Tooltip>
                                    <Tooltip title="Adjust Quantity">
                                        <IconButton size="small" color="primary" onClick={() => { setSelectedReagent(r); setAdjustDialog(true); }}><SwapVertIcon fontSize="small" /></IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                        {filtered.length === 0 && (
                            <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary' }}>No reagents found</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create Reagent Dialog */}
            <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Reagent</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                    <TextField label="Name" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} size="small" />
                    <TextField label="Catalog Number" value={form.catalogNumber} onChange={e => setForm({ ...form, catalogNumber: e.target.value })} size="small" />
                    <TextField label="Manufacturer" value={form.manufacturer} onChange={e => setForm({ ...form, manufacturer: e.target.value })} size="small" />
                    <TextField label="Supplier" value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} size="small" />
                    <TextField label="Lot Number" value={form.lotNumber} onChange={e => setForm({ ...form, lotNumber: e.target.value })} size="small" />
                    <TextField label="Expiry Date" type="date" InputLabelProps={{ shrink: true }} value={form.expiryDate} onChange={e => setForm({ ...form, expiryDate: e.target.value })} size="small" />
                    <TextField label="Received Date" type="date" InputLabelProps={{ shrink: true }} value={form.receivedDate} onChange={e => setForm({ ...form, receivedDate: e.target.value })} size="small" />
                    <TextField label="Quantity Received" type="number" value={form.quantityReceived} onChange={e => setForm({ ...form, quantityReceived: e.target.value })} size="small" />
                    <FormControl size="small">
                        <InputLabel>Unit</InputLabel>
                        <Select value={form.unit} label="Unit" onChange={e => setForm({ ...form, unit: e.target.value })}>
                            <MenuItem value="tests">Tests</MenuItem>
                            <MenuItem value="mL">mL</MenuItem>
                            <MenuItem value="L">L</MenuItem>
                            <MenuItem value="units">Units</MenuItem>
                            <MenuItem value="bottles">Bottles</MenuItem>
                            <MenuItem value="kits">Kits</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField label="Reorder Point" type="number" value={form.reorderPoint} onChange={e => setForm({ ...form, reorderPoint: e.target.value })} size="small" />
                    <TextField label="Reorder Quantity" type="number" value={form.reorderQuantity} onChange={e => setForm({ ...form, reorderQuantity: e.target.value })} size="small" />
                    <TextField label="Cost Per Unit" type="number" value={form.costPerUnit} onChange={e => setForm({ ...form, costPerUnit: e.target.value })} size="small" />
                    <TextField label="Expiry Alert Days" type="number" value={form.expiryAlertDays} onChange={e => setForm({ ...form, expiryAlertDays: e.target.value })} size="small" helperText="Days before expiry to trigger alerts" />
                    <TextField label="Storage Temperature" value={form.storageTemp} onChange={e => setForm({ ...form, storageTemp: e.target.value })} size="small" placeholder="e.g. Refrigerated (2-8C)" />
                    <TextField label="Storage Location" value={form.storageLocation} onChange={e => setForm({ ...form, storageLocation: e.target.value })} size="small" />
                    <FormControl size="small">
                        <InputLabel>Department</InputLabel>
                        <Select value={form.departmentId} label="Department" onChange={e => setForm({ ...form, departmentId: e.target.value })}>
                            {departments.map(d => <MenuItem key={d._id} value={d._id}>{d.name}</MenuItem>)}
                        </Select>
                    </FormControl>

                    <Typography variant="subtitle2" sx={{ mt: 1 }}>Associated Tests</Typography>
                    <Autocomplete
                        options={tests}
                        getOptionLabel={(option) => `${option.testCode} - ${option.name}`}
                        onChange={handleAddAssociatedTest}
                        value={null}
                        renderInput={(params) => <TextField {...params} label="Search and add test" size="small" />}
                        size="small"
                    />
                    {form.associatedTests.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {form.associatedTests.map(a => (
                                <Box key={a.testId} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label={a.testLabel} onDelete={() => handleRemoveAssociatedTest(a.testId)} size="small" sx={{ flex: 1 }} />
                                    <TextField
                                        label="Per Test"
                                        type="number"
                                        value={a.consumptionPerTest}
                                        onChange={e => handleConsumptionChange(a.testId, e.target.value)}
                                        size="small"
                                        sx={{ width: 100 }}
                                        inputProps={{ min: 0, step: 0.1 }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreate} disabled={!form.name || loading}>Create</Button>
                </DialogActions>
            </Dialog>

            {/* Edit Reagent Dialog */}
            <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Reagent</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                    <TextField label="Name" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} size="small" />
                    <TextField label="Catalog Number" value={form.catalogNumber} onChange={e => setForm({ ...form, catalogNumber: e.target.value })} size="small" />
                    <TextField label="Manufacturer" value={form.manufacturer} onChange={e => setForm({ ...form, manufacturer: e.target.value })} size="small" />
                    <TextField label="Supplier" value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} size="small" />
                    <TextField label="Lot Number" value={form.lotNumber} onChange={e => setForm({ ...form, lotNumber: e.target.value })} size="small" />
                    <TextField label="Expiry Date" type="date" InputLabelProps={{ shrink: true }} value={form.expiryDate} onChange={e => setForm({ ...form, expiryDate: e.target.value })} size="small" />
                    <TextField label="Received Date" type="date" InputLabelProps={{ shrink: true }} value={form.receivedDate} onChange={e => setForm({ ...form, receivedDate: e.target.value })} size="small" />
                    <FormControl size="small">
                        <InputLabel>Unit</InputLabel>
                        <Select value={form.unit} label="Unit" onChange={e => setForm({ ...form, unit: e.target.value })}>
                            <MenuItem value="tests">Tests</MenuItem>
                            <MenuItem value="mL">mL</MenuItem>
                            <MenuItem value="L">L</MenuItem>
                            <MenuItem value="units">Units</MenuItem>
                            <MenuItem value="bottles">Bottles</MenuItem>
                            <MenuItem value="kits">Kits</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField label="Reorder Point" type="number" value={form.reorderPoint} onChange={e => setForm({ ...form, reorderPoint: e.target.value })} size="small" />
                    <TextField label="Reorder Quantity" type="number" value={form.reorderQuantity} onChange={e => setForm({ ...form, reorderQuantity: e.target.value })} size="small" />
                    <TextField label="Cost Per Unit" type="number" value={form.costPerUnit} onChange={e => setForm({ ...form, costPerUnit: e.target.value })} size="small" />
                    <TextField label="Expiry Alert Days" type="number" value={form.expiryAlertDays} onChange={e => setForm({ ...form, expiryAlertDays: e.target.value })} size="small" helperText="Days before expiry to trigger alerts" />
                    <TextField label="Storage Temperature" value={form.storageTemp} onChange={e => setForm({ ...form, storageTemp: e.target.value })} size="small" placeholder="e.g. Refrigerated (2-8C)" />
                    <TextField label="Storage Location" value={form.storageLocation} onChange={e => setForm({ ...form, storageLocation: e.target.value })} size="small" />
                    <FormControl size="small">
                        <InputLabel>Department</InputLabel>
                        <Select value={form.departmentId} label="Department" onChange={e => setForm({ ...form, departmentId: e.target.value })}>
                            {departments.map(d => <MenuItem key={d._id} value={d._id}>{d.name}</MenuItem>)}
                        </Select>
                    </FormControl>

                    <Typography variant="subtitle2" sx={{ mt: 1 }}>Associated Tests</Typography>
                    <Autocomplete
                        options={tests}
                        getOptionLabel={(option) => `${option.testCode} - ${option.name}`}
                        onChange={handleAddAssociatedTest}
                        value={null}
                        renderInput={(params) => <TextField {...params} label="Search and add test" size="small" />}
                        size="small"
                    />
                    {form.associatedTests.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {form.associatedTests.map(a => (
                                <Box key={a.testId} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label={a.testLabel} onDelete={() => handleRemoveAssociatedTest(a.testId)} size="small" sx={{ flex: 1 }} />
                                    <TextField
                                        label="Per Test"
                                        type="number"
                                        value={a.consumptionPerTest}
                                        onChange={e => handleConsumptionChange(a.testId, e.target.value)}
                                        size="small"
                                        sx={{ width: 100 }}
                                        inputProps={{ min: 0, step: 0.1 }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleUpdate} disabled={!form.name || loading}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* Adjust Quantity Dialog */}
            <Dialog open={adjustDialog} onClose={() => setAdjustDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Adjust Quantity - {selectedReagent?.name}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                    {selectedReagent && (
                        <Typography variant="body2" color="text.secondary">
                            Current: {selectedReagent.quantityRemaining} {selectedReagent.unit}
                        </Typography>
                    )}
                    <TextField label="Adjustment" type="number" required value={adjustForm.adjustment} onChange={e => setAdjustForm({ ...adjustForm, adjustment: e.target.value })} size="small" helperText="Positive to add, negative to subtract" />
                    <TextField label="Reason" required multiline rows={2} value={adjustForm.reason} onChange={e => setAdjustForm({ ...adjustForm, reason: e.target.value })} size="small" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAdjustDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleAdjust} disabled={!adjustForm.adjustment || !adjustForm.reason || loading}>Adjust</Button>
                </DialogActions>
            </Dialog>

            {/* Alerts Dialog */}
            <Dialog open={alertsDialog} onClose={() => setAlertsDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Reagent Alerts</DialogTitle>
                <DialogContent>
                    {alerts.expired?.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'error.main', mb: 1 }}>Expired ({alerts.expired.length})</Typography>
                            {alerts.expired.map(r => (
                                <Alert key={r._id} severity="error" sx={{ mb: 1 }}>
                                    <strong>{r.name}</strong> (Lot: {r.lotNumber || 'N/A'}) - Expired {new Date(r.expiryDate).toLocaleDateString()}
                                </Alert>
                            ))}
                        </Box>
                    )}
                    {alerts.expiringSoon?.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'warning.main', mb: 1 }}>Expiring Soon ({alerts.expiringSoon.length})</Typography>
                            {alerts.expiringSoon.map(r => (
                                <Alert key={r._id} severity="warning" sx={{ mb: 1 }}>
                                    <strong>{r.name}</strong> (Lot: {r.lotNumber || 'N/A'}) - Expires {new Date(r.expiryDate).toLocaleDateString()}
                                </Alert>
                            ))}
                        </Box>
                    )}
                    {alerts.lowStock?.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'warning.main', mb: 1 }}>Low Stock ({alerts.lowStock.length})</Typography>
                            {alerts.lowStock.map(r => (
                                <Alert key={r._id} severity="warning" sx={{ mb: 1 }}>
                                    <strong>{r.name}</strong> - {r.quantityRemaining} {r.unit} remaining (reorder at {r.reorderPoint})
                                </Alert>
                            ))}
                        </Box>
                    )}
                    {totalAlerts === 0 && (
                        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No active alerts</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAlertsDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Usage History Dialog */}
            <Dialog open={historyDialog} onClose={() => { setHistoryDialog(false); dispatch(resetGetUsageHistory()); }} maxWidth="md" fullWidth>
                <DialogTitle>Usage History - {selectedReagent?.name}</DialogTitle>
                <DialogContent>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell align="right">Change</TableCell>
                                    <TableCell align="right">Balance</TableCell>
                                    <TableCell>Reason</TableCell>
                                    <TableCell>Test</TableCell>
                                    <TableCell>User</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {usageHistory.map(log => (
                                    <TableRow key={log._id}>
                                        <TableCell>{new Date(log.createdAt).toLocaleString()}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={changeTypeLabels[log.changeType] || log.changeType}
                                                size="small"
                                                color={log.changeType === 'restock' ? 'success' : log.changeType === 'test_consumption' ? 'primary' : 'default'}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: log.quantityChange > 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>
                                            {log.quantityChange > 0 ? '+' : ''}{log.quantityChange}
                                        </TableCell>
                                        <TableCell align="right">{log.quantityBefore} &rarr; {log.quantityAfter}</TableCell>
                                        <TableCell>{log.reason || '-'}</TableCell>
                                        <TableCell>{log.testId ? `${log.testId.testCode || ''} ${log.testId.name || ''}`.trim() : '-'}</TableCell>
                                        <TableCell>{log.performedBy?.name || log.performedBy?.firstName || '-'}</TableCell>
                                    </TableRow>
                                ))}
                                {usageHistory.length === 0 && (
                                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>No usage history</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={historyPagination.total}
                        page={historyPage}
                        onPageChange={(_, newPage) => {
                            setHistoryPage(newPage);
                            if (selectedReagent) dispatch(getUsageHistoryRequest({ id: selectedReagent._id, page: newPage + 1, limit: historyRowsPerPage }));
                        }}
                        rowsPerPage={historyRowsPerPage}
                        onRowsPerPageChange={(e) => {
                            const newLimit = parseInt(e.target.value, 10);
                            setHistoryRowsPerPage(newLimit);
                            setHistoryPage(0);
                            if (selectedReagent) dispatch(getUsageHistoryRequest({ id: selectedReagent._id, page: 1, limit: newLimit }));
                        }}
                        rowsPerPageOptions={[10, 25, 50]}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setHistoryDialog(false); dispatch(resetGetUsageHistory()); }}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
